import labelControl from '@helpers/dom/labelControl';
import Modes from '@config/modes';
import type CustomEmojiElement from '@lib/customEmoji/element';
import {createUniqueId} from 'solid-js';
import type {AnimationItemGroup} from '@components/animationIntersector';
import {CustomEmojiRendererElement} from '@lib/customEmoji/renderer';
import {bindActiveWindowListener} from '@helpers/appWindow';
import cancelEvent from '@helpers/dom/cancelEvent';
import simulateEvent from '@helpers/dom/dispatchEvent';
import documentFragmentToHTML from '@helpers/dom/documentFragmentToHTML';
import findUpAttribute from '@helpers/dom/findUpAttribute';
import findUpTag from '@helpers/dom/findUpTag';
import getCaretPosNew from '@helpers/dom/getCaretPosNew';
import getRichValueWithCaret from '@helpers/dom/getRichValueWithCaret';
import isInputEmpty, {isInputPlaceholderEmpty} from '@helpers/dom/isInputEmpty';
import replaceContent from '@helpers/dom/replaceContent';
import RichInputHandler, {USING_BOMS} from '@helpers/dom/richInputHandler';
import selectElementContents from '@helpers/dom/selectElementContents';
import setInnerHTML, {setDirection} from '@helpers/dom/setInnerHTML';
import {MessageEntity} from '@layer';
import type {LocalTextWithEntities} from '@types';
import {i18n, LangPackKey, _i18n} from '@lib/langPack';
import {NULL_PEER_ID} from '@appManagers/constants';
import parseEntities from '@lib/richTextProcessor/parseEntities';
import wrapDraftText from '@lib/richTextProcessor/wrapDraftText';
import {createCustomFiller, insertCustomFillers} from '@lib/richTextProcessor/wrapRichText';
import type {MarkupTooltipTypes} from '@components/chat/markupTooltip';
import {getChatInputEditor} from '@components/chat/inputEditor/registry';
import {isProseMirrorPaste} from '@components/chat/inputEditor/paste';

/**
 * What a field can be filled with. Text plus entities is the representation the
 * app already holds wherever it loads a draft, a message being edited or a fact
 * check; rendering it into DOM only to parse it back is what the editor made
 * unnecessary, so the setter takes it as is and each backend renders it once.
 *
 * A field without an editor still renders it to DOM here, with no animation
 * group and no middleware — one that has custom emoji to keep alive for a
 * limited time builds its own node and passes that instead.
 */
export type InputFieldValue = Parameters<typeof replaceContent>[1] | LocalTextWithEntities;

// A node always carries a `nodeType`; asking for one instead of `instanceof Node`
// also answers for a node from the popped-out (Document PiP) window.
function asTextWithEntities(value: InputFieldValue) {
  return value && typeof(value) === 'object' && (value as Node).nodeType === undefined ?
    value as LocalTextWithEntities :
    undefined;
}

export async function insertRichTextAsHTML(input: HTMLElement, text: string, entities: MessageEntity[], wrappingForPeerId?: PeerId) {
  const editor = getChatInputEditor(input);
  if(editor) {
    editor.replaceSelection(text, entities);
    return;
  }

  const loadPromises: Promise<any>[] = [];
  const wrappingCustomEmoji = entities?.some((entity) => entity._ === 'messageEntityCustomEmoji');
  const renderer = wrappingCustomEmoji ? createCustomEmojiRendererForInput() : undefined;
  const fragment = wrapDraftText(text, {entities, wrappingForPeerId, loadPromises, customEmojiRenderer: renderer});
  const something = fragment.querySelectorAll<HTMLElement>('[contenteditable="false"]');
  something.forEach((el) => {
    el.contentEditable = 'inherit';
    el.classList.add('pc');
  });

  loadPromises.length && await Promise.all(loadPromises);

  fragment.querySelectorAll<HTMLElement>('.input-selectable').forEach((el) => {
    el.prepend(createCustomFiller(true));
  });

  const richInputHandler = USING_BOMS ? RichInputHandler.getInstance() : undefined;
  // const restore = richInputHandler.prepareApplyingMarkdown();

  // fragment.querySelectorAll('.input-filler').forEach((el) => el.remove());

  // const fillers = Array.from(input.querySelectorAll<HTMLElement>('.input-filler')).map((el) => {
  //   el.contentEditable = 'false';
  //   return el;
  // });

  const customEmojiElements = Array.from(fragment.querySelectorAll<HTMLImageElement>('.custom-emoji-placeholder')).map((el) => {
    el.dataset.ces = '1';
    return (el as any).customEmojiElement as CustomEmojiElement;
  });

  const html = documentFragmentToHTML(fragment);
  renderer?.destroy();

  // console.log(html);

  const pre = getCaretPosNew(input);
  // console.log('pre', pre);
  let textNode: ChildNode, textNodeValue: string;
  if(pre.node) {
    // if(pre.node?.nodeValue === BOM && false) {
    //   textNode = document.createTextNode(textNodeValue = BOM);
    //   (pre.node.parentNode as any as ChildNode).after(textNode);
    //   pre.selection.modify('extend', 'forward', 'character');
    //   pre.selection.collapseToEnd();
    // }
  } else {
    const range = input.ownerDocument.createRange();
    let node = input.lastChild;
    if(!node) {
      input.append(node /* = textNode */ = input.ownerDocument.createTextNode(''));
    }

    range.setStartAfter(node);
    range.collapse(true);
    pre.selection.removeAllRanges();
    pre.selection.addRange(range);
  }

  // const fragmentLastChild = fragment.lastChild;

  // const s = document.createElement('span');
  // (node as ChildNode).replaceWith(s);
  // s.append(node);
  input.addEventListener('input', cancelEvent, {capture: true, once: true, passive: false});
  richInputHandler?.onBeforeInput({inputType: 'insertContent'});
  input.ownerDocument.execCommand('insertHTML', false, html);
  Array.from(input.querySelectorAll<HTMLImageElement>('[data-ces]')).forEach((el, idx) => {
    delete el.dataset.ces;
    const customEmojiElement = customEmojiElements[idx];
    (el as any).customEmojiElement = customEmojiElement;
    customEmojiElement.placeholder = el;
  });
  // fillers.forEach((el) => {
  //   el.contentEditable = 'inherit';
  // });
  input.querySelectorAll<HTMLElement>('.pc').forEach((el) => {
    el.contentEditable = 'false';
  });
  if(textNode) {
    const {nodeValue} = textNode;
    if(nodeValue === textNodeValue) {
      textNode.remove();
    } else {
      (textNode as CharacterData).replaceData(nodeValue.indexOf(textNodeValue), textNodeValue.length, '');
    }
  }
  // restore();
  richInputHandler?.removeExtraBOMs(input);
  simulateEvent(input, 'input');

  // if(textNode) {
  //   const selection = document.getSelection();
  //   const node = fragmentLastChild.nextSibling || fragmentLastChild;
  //   const range = document.createRange();
  //   range.setStartAfter(node);
  //   range.collapse(true);
  //   selection.removeAllRanges();
  //   selection.addRange(range);
  // }

  // const after = getCaretPosNew(input);
  // console.log('after', after);
  // if(after.node?.nodeValue === BOM) {
  //   const smth = findUpClassName(after.node.parentElement, 'input-something');
  //   if(smth) {
  //     const selection = document.getSelection();
  //     const node = smth.nextSibling;
  //     const range = document.createRange();
  //     range.setStartAfter(node);
  //     range.collapse(true);
  //     selection.removeAllRanges();
  //     selection.addRange(range);

  //     // if(after.offset === 0) after.selection.modify('extend', 'forward', 'character');
  //     // after.selection.modify('extend', 'forward', 'character');
  //     // after.selection.collapseToEnd();
  //   }
  // }

  // setCaretAt(fragmentLastChild.nextSibling);

  // console.log('ASD');
}

let init = () => {
  // Paste into a contenteditable that has no editor. Such a field carries no
  // formatting — its value is a string plus, at most, emoji — so the clipboard's
  // HTML has nothing to land in, and what the source itself calls plain text is
  // the content. Follow the active window so paste into a popped-out
  // (Document PiP) input is still intercepted.
  bindActiveWindowListener((w) => w.document, 'paste', (e) => {
    const input = findUpAttribute(e.target, 'contenteditable="true"');
    if(!input) {
      return;
    }
    if(getChatInputEditor(input) || isProseMirrorPaste(e)) return;

    e.preventDefault();

    let text = e.clipboardData.getData('text/plain').replace(/\r/g, '');
    // A field built without linebreaks has one line to put this on.
    if(input.dataset.noLinebreaks) text = text.replace(/\n/g, '');

    const peerId = (input.dataset.peerId || NULL_PEER_ID).toPeerId();
    const entities = parseEntities(text).filter((entity) => entity._ === 'messageEntityEmoji');
    insertRichTextAsHTML(input, text, entities, peerId);
  });

  init = null;
};

// ! it doesn't respect symbols other than strongs
/* const checkAndSetRTL = (input: HTMLElement) => {
  //const isEmpty = isInputEmpty(input);
  //console.log('input', isEmpty);

  //const char = [...getRichValue(input)][0];
  const char = (input instanceof HTMLInputElement ? input.value : input.innerText)[0];
  let direction = 'ltr';
  if(char && checkRTL(char)) {
    direction = 'rtl';
  }

  //console.log('RTL', direction, char);

  input.style.direction = direction;
}; */

export enum InputState {
  Neutral = 0,
  Valid = 1,
  Error = 2
};

export type InputFieldOptions = {
  placeholder?: LangPackKey,
  placeholderAsElement?: boolean,
  label?: LangPackKey,
  labelOptions?: any[],
  labelText?: string | DocumentFragment,
  name?: string,
  maxLength?: number,
  showLengthOn?: number,
  plainText?: true,
  required?: boolean,
  canBeEdited?: boolean,
  validate?: () => boolean,
  inputMode?: 'tel' | 'numeric',
  withLinebreaks?: boolean,
  autocomplete?: string,
  withBorder?: boolean,
  allowStartingSpace?: boolean,
  onRawInput?: (value: string) => void,
  canHaveFormatting?: Array<MarkupTooltipTypes>
  canWrapCustomEmojis?: boolean;
};

function createCustomEmojiRendererForInput(textColor?: string, animationGroup?: AnimationItemGroup) {
  const renderer = CustomEmojiRendererElement.create({
    wrappingDraft: true,
    isSelectable: true,
    textColor: textColor || 'primary-text-color',
    animationGroup
  });

  return renderer;
}

function processCustomEmojisInInput(input: HTMLElement) {
  const customEmojiElements = Array.from(input.querySelectorAll<CustomEmojiElement | HTMLElement>('.custom-emoji, .custom-emoji-placeholder'));
  let renderer = input.querySelector<CustomEmojiRendererElement>('.custom-emoji-renderer');
  if(!renderer && customEmojiElements.length) {
    renderer = createCustomEmojiRendererForInput(input.dataset.textColor, input.dataset.animationGroup as AnimationItemGroup);
    input.prepend(renderer);
  } else if(renderer && !customEmojiElements.length) {
    renderer.remove();
    return;
  }

  if(!renderer) {
    return;
  }

  const customEmojis: Parameters<CustomEmojiRendererElement['add']>[0]['addCustomEmojis'] = new Map();
  customEmojiElements.forEach((element) => {
    const customEmojiElement: CustomEmojiElement = (element as CustomEmojiElement).loop !== undefined ? element as CustomEmojiElement : (element as any).customEmojiElement as CustomEmojiElement;
    const {docId} = customEmojiElement;
    let set = customEmojis.get(docId);
    if(!set) {
      customEmojis.set(docId, set = new Set());
    }

    set.add(customEmojiElement);
  });

  for(const [docId, customEmojiElements] of customEmojis) {
    let hasSet = renderer.customEmojis.get(docId);
    if(hasSet) {
      for(const customEmojiElement of hasSet) {
        if(!customEmojiElements.has(customEmojiElement)) {
          customEmojiElement.destroy();
        }
      }
    } else {
      hasSet = new Set();
    }

    for(const customEmojiElement of customEmojiElements) {
      if(!hasSet.has(customEmojiElement)) {
        customEmojiElement.connectedCallback();
      }
    }
  }

  renderer.add({
    addCustomEmojis: customEmojis,
    lazyLoadQueue: false
  });
  renderer.forceRender();
}

let inputFieldErrorIdSeed = 0;

export default class InputField {
  public container: HTMLElement;
  public input: HTMLElement;
  public label: HTMLLabelElement;
  public placeholder: HTMLElement;
  public errorLabel: HTMLElement;

  public originalValue: string;

  public required: boolean;
  public validate: () => boolean;

  public allowStartingSpace: boolean;

  private isInputHidden = false;

  constructor(public options: InputFieldOptions = {}) {
    this.container = document.createElement('div');
    this.container.classList.add('input-field');

    this.required = options.required;
    this.validate = options.validate;

    if(options.maxLength !== undefined && options.showLengthOn === undefined) {
      options.showLengthOn = Math.min(40, Math.round(options.maxLength / 3));
    }

    const {placeholder, maxLength, showLengthOn, name, plainText, canBeEdited = true, autocomplete, withBorder, allowStartingSpace, canHaveFormatting, canWrapCustomEmojis} = options;
    const label = options.label || options.labelText;
    this.allowStartingSpace = allowStartingSpace;

    const onInputCallbacks: Array<() => void> = [];
    let input: HTMLElement;
    if(!plainText) {
      if(init) {
        init();
      }

      this.container.innerHTML = `<div class="input-field-input"></div>`;

      input = this.container.firstElementChild as HTMLElement;
      input.contentEditable = '' + !!canBeEdited;
      input.setAttribute('role', 'textbox');
      input.setAttribute('aria-multiline', `${!!options.withLinebreaks}`);
      if(!canBeEdited) input.setAttribute('aria-readonly', 'true');
      // * browser & extension translators rewrite the text nodes right inside the contenteditable,
      // * so the value read back from the DOM is the translated one — with broken entities and custom emojis
      input.translate = false;
      // const observer = new MutationObserver(() => {
      //   //checkAndSetRTL(input);

      //   if(processInput) {
      //     processInput();
      //   }
      // });

      RichInputHandler.getInstance();

      input.addEventListener('mousedown', (e) => {
        if(getChatInputEditor(input)) return;

        const selection = input.ownerDocument.defaultView.getSelection();
        if(!selection.isCollapsed) {
          return;
        }

        const placeholder = findUpTag(e.target, 'IMG');
        if(!placeholder) {
          return;
        }

        const rect = placeholder.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const focusOnNext = e.clientX >= centerX;

        const range = input.ownerDocument.createRange();
        range.setStartAfter(focusOnNext ? placeholder : placeholder.previousSibling ?? placeholder);
        selection.removeAllRanges();
        selection.addRange(range);
      });

      if(canHaveFormatting) {
        input.setAttribute('can-format', canHaveFormatting.join(','));
      }

      onInputCallbacks.push(() => {
        // console.log('input');
        // return;
        // * because if delete all characters there will br left
        const isEmpty = this.isEmpty();
        const editor = getChatInputEditor(input);
        if(isEmpty && !editor) {
          // const textNode = Array.from(input.childNodes).find((node) => node.nodeType === node.TEXT_NODE) || document.createTextNode('');
          input.replaceChildren();
          // input.append(document.createTextNode('')); // need first text node to support history stack
        }

        this.setEmpty();

        // const fillers = Array.from(input.querySelectorAll('.emoji-filler')) as HTMLElement[];
        // fillers.forEach((filler) => {
        //   const textContent = filler.textContent;
        //   if(textContent === BOM) {
        //     return;
        //   }

        //   if(textContent) {

        //   } else {
        //     let curChild = filler.firstChild;
        //     while(curChild) {
        //       curChild = curChild.nextSibling;
        //     }
        //   }

        //   filler.classList.remove('emoji-filler');
        // });

        // Array.from(input.querySelectorAll('br, span:empty')).forEach((el) => {
        //   const parentElement = el.parentElement;
        //   (parentElement === input ? el : parentElement).remove();
        // });
        !editor && USING_BOMS && Array.from(input.querySelectorAll('br:not(.br-not-br)')).forEach((el) => {
          // const parentElement = el.parentElement;
          el.remove();
          // if(!parentElement.children.length && !parentElement.textContent) {
          //   parentElement.textContent = '';
          // }
        });

        if(!editor) {
          insertCustomFillers(Array.from(input.querySelectorAll('.input-something')));
          processCustomEmojisInInput(input);
        }

        // .forEach((el) => el.remove());
      });

      // ! childList for paste first symbol
      // observer.observe(input, {characterData: true, childList: true, subtree: true});
    } else {
      this.container.innerHTML = `
      <input type="text" ${name ? `name="${name}"` : ''} autocomplete="${autocomplete ?? 'off'}" ${label ? 'required=""' : ''} class="input-field-input">
      `;

      input = this.container.firstElementChild as HTMLElement;
      // input.addEventListener('input', () => checkAndSetRTL(input));

      onInputCallbacks.push(() => {
        const isEmpty = this.isEmpty();
        if(isEmpty) {
          (input as HTMLInputElement).value = '';
        }

        this.setEmpty();
      });
    }

    setDirection(input);

    if(options.inputMode) {
      input.inputMode = options.inputMode;
    }

    if(placeholder) {
      // if(options.placeholderAsElement) {
      this.placeholder = document.createElement('span');
      this.placeholder.id = createUniqueId();
      if(!label) input.setAttribute('aria-labelledby', this.placeholder.id);
      this.placeholder.classList.add('input-field-placeholder');
      this.container.append(this.placeholder);
      _i18n(this.placeholder, placeholder, undefined);
      // } else {
      //   _i18n(input, placeholder, undefined, 'placeholder');
      // }
    }

    if(withBorder !== false && withBorder || label || placeholder) {
      const border = document.createElement('div');
      border.classList.add('input-field-border');
      this.container.append(border);
    }

    if(label != null || maxLength) {
      this.label = document.createElement('label');
      this.label.id = createUniqueId();
      input.setAttribute(label ? 'aria-labelledby' : 'aria-describedby', this.label.id);
      if(input.tagName === 'INPUT' || input.tagName === 'TEXTAREA') {
        input.id ||= createUniqueId();
        this.label.htmlFor = input.id;
      }
      this.setLabel();
      this.container.append(this.label);
    }

    // Give the control an accessible name by associating it with the floating
    // label (or, failing that, the placeholder). `aria-labelledby` works for
    // both the plain <input> and the contenteditable rich-text div.
    const namingElement = this.label || this.placeholder;
    labelControl(input, namingElement);

    if(maxLength) {
      const labelEl = this.label;
      let showingLength = false;

      const onInput = () => {
        const wasError = input.classList.contains('error');
        // * https://stackoverflow.com/a/54369605 #2 to count emoji as 1 symbol
        const inputLength = plainText ?
          (input as HTMLInputElement).value.length :
          [...getRichValueWithCaret(input, false, false).value].length;
        const diff = maxLength - inputLength;
        const isError = diff < 0;
        input.classList.toggle('error', isError);
        labelEl.classList.toggle('error', isError);

        // this.onLengthChange && this.onLengthChange(inputLength, isError);

        if(isError || diff <= showLengthOn) {
          this.setLabel();
          labelEl.append(` (${maxLength - inputLength})`);
          labelEl.style.visibility = 'visible';
          if(!showingLength) showingLength = true;
        } else if((wasError && !isError) || showingLength) {
          this.setLabel();
          showingLength = false;
        }
      };

      onInputCallbacks.push(onInput);
    }

    const noLinebreaks = !options.withLinebreaks;
    if(noLinebreaks && !plainText) {
      input.dataset.noLinebreaks = '1';
      input.addEventListener('keypress', (e) => {
        if(e.key === 'Enter') {
          e.preventDefault();
          return false;
        }
      });
    }

    if(options.onRawInput) {
      onInputCallbacks.push(() => {
        options.onRawInput(this.value);
      });
    }

    if(onInputCallbacks.length) {
      input.addEventListener('input', () => {
        onInputCallbacks.forEach((callback) => callback());
      });
    }

    if(canWrapCustomEmojis) input.dataset.canWrapCustomEmojis = '1';

    this.input = input;
    this.setEmpty(true);
  }

  public select() {
    if(!this.value) { // * avoid selecting whole empty field on iOS devices
      return;
    }

    if(this.options.plainText) {
      (this.input as HTMLInputElement).select(); // * select text
    } else {
      selectElementContents(this.input);
    }
  }

  public setLabel() {
    this.label.textContent = '';
    if(this.options.labelText) {
      setInnerHTML(this.label, this.options.labelText);
    } else if(this.options.label) {
      this.label.append(i18n(this.options.label, this.options.labelOptions));
    }
    this.label.style.visibility = this.label.textContent ? 'visible' : 'hidden';
  }

  get value(): string {
    return this.options.plainText ?
      (this.input as HTMLInputElement).value :
      getRichValueWithCaret(this.input, false, false).value;
    // return getRichValue(this.input);
  }

  set value(value: InputFieldValue) {
    this.setValueSilently(value, true);
    this.simulateInputEvent();
  }

  public simulateInputEvent() {
    simulateEvent(this.input, 'input');
  }

  public syncFromInput() {
    this.setEmpty();
  }

  public setValueSilently(value: InputFieldValue, fromSet?: boolean) {
    if(this.options.plainText) {
      (this.input as HTMLInputElement).value = value as string;
    } else {
      const textWithEntities = asTextWithEntities(value);
      const editor = getChatInputEditor(this.input);
      if(editor) {
        if(textWithEntities) editor.setTextWithEntities(textWithEntities.text, textWithEntities.entities);
        else editor.setContent(value as Parameters<typeof replaceContent>[1]);
      } else {
        replaceContent(this.input, textWithEntities ? wrapDraftText(textWithEntities.text, {
          entities: textWithEntities.entities,
          wrappingForPeerId: this.input.dataset.peerId?.toPeerId()
        }) : value as Parameters<typeof replaceContent>[1]);
        processCustomEmojisInInput(this.input);
      }
    }

    this.setEmpty();
  }

  private setEmpty = (empty = this.isPlaceholderEmpty()) => {
    [this.input, this.placeholder].filter(Boolean).forEach((el) => {
      el.classList.toggle('is-empty', empty);
    });
  };

  private isPlaceholderEmpty() {
    return isInputPlaceholderEmpty(this.input, this.allowStartingSpace) || this.isInputHidden;
  }

  public setHidden(hidden: boolean) {
    this.isInputHidden = hidden;
    this.setEmpty();
  }

  public isEmpty() {
    return isInputEmpty(this.input, this.allowStartingSpace) || this.isInputHidden;
  }

  public isChanged() {
    return this.value !== this.originalValue;
  }

  public isValid() {
    return !this.input.classList.contains('error') &&
      (!this.validate || this.validate()) &&
      (!this.required || !this.isEmpty());
  }

  public isValidToChange() {
    return this.isValid() && this.isChanged();
  }

  public setDraftValue(value = '', silent?: boolean) {
    let _value: Parameters<typeof replaceContent>[1] = value;
    if(!this.options.plainText) {
      _value = /* documentFragmentToHTML */(wrapDraftText(value));
    }

    if(silent) {
      this.setValueSilently(_value, false);
    } else {
      this.value = _value;
    }
  }

  public setOriginalValue(value: InputField['originalValue'] = '', silent?: boolean) {
    this.originalValue = value;
    this.setDraftValue(value, silent);
  }

  private removeErrorLabel() {
    if(!this.errorLabel) return;

    const describedBy = (this.input.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((id) => id && id !== this.errorLabel.id);

    if(describedBy.length) {
      this.input.setAttribute('aria-describedby', describedBy.join(' '));
    } else {
      this.input.removeAttribute('aria-describedby');
    }

    this.errorLabel.remove();
    this.errorLabel = undefined;
    this.container.classList.remove('has-error-label');
  }

  public setState(state: InputState, label?: LangPackKey, labelOptions?: any[]) {
    const isError = !!(state & InputState.Error);

    // Without the a11y layer the error replaces the floating label's text, as it
    // always did; the layer gives it an element of its own to announce.
    if(label && isError && Modes.a11y) {
      if(!this.errorLabel) {
        this.errorLabel = this.container.ownerDocument.createElement('span');
        this.errorLabel.id = 'input-field-error-' + (++inputFieldErrorIdSeed);
        this.errorLabel.classList.add('input-field-error-label');
        this.errorLabel.setAttribute('role', 'alert');
        this.container.append(this.errorLabel);
      }

      this.errorLabel.replaceChildren(i18n(label, labelOptions ?? this.options.labelOptions));
      this.container.classList.add('has-error-label');

      const describedBy = new Set(
        (this.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean)
      );
      describedBy.add(this.errorLabel.id);
      this.input.setAttribute('aria-describedby', [...describedBy].join(' '));
    } else if(label) {
      this.label.textContent = '';
      this.label.append(i18n(label, labelOptions ?? this.options.labelOptions));
      this.label.style.visibility = 'visible';
    } else {
      this.setLabel();
    }

    if((!isError || !label) && this.errorLabel) {
      this.removeErrorLabel();
    }

    if(isError) {
      this.input.setAttribute('aria-invalid', 'true');
    } else {
      this.input.removeAttribute('aria-invalid');
    }
    this.input.classList.toggle('error', isError);
    this.input.classList.toggle('valid', !!(state & InputState.Valid));
  }

  public setError(label?: LangPackKey, labelOptions?: any[]) {
    this.setState(InputState.Error, label, labelOptions);
  }

  public toggleForceFocus(enabled: boolean) {
    this.input.classList.toggle('force-focus', enabled)
  }
}
